import {
    CalendarEventTypesFragment,
    CategoriesMultilistFieldFragment,
    ContentBlockFragment,
    HashTagsMultilistFieldFragment,
} from '@graphqlTypes/sitecore';
import { sortContentBlocksByEventDate } from '@src/common/scores/sort-contents';
import { RacingScheduleItem } from '@src/utils/js-bridge/js-bridge';
import { DeepReadonly } from '@src/utils/type-utils';
import {
    addWeeks,
    format,
    isSameMonth,
    parse,
    startOfMonth,
    subWeeks,
    startOfWeek,
    isBefore,
    isValid,
} from 'date-fns';
import { makeAutoObservable } from 'mobx';
import { AppBaseStore } from './app-base-store';
import { SettingStore } from './setting-store';

export class CalendarStore {
    selectedEventType = '';
    selectedDate: Date | undefined = new Date();
    currentWeek = startOfWeek(Date.now());
    currentMonth = startOfMonth(Date.now());
    isOneWeekMode = true;
    constructor(private baseStore: AppBaseStore, private settingStore: SettingStore) {
        makeAutoObservable(this, undefined, { deep: false });
    }

    get filteredContentBlocks(): Record<string, ReadonlyArray<ContentBlockWithSection>> {
        if (!this.selectedEventType) {
            return { ...this.contentBlocks };
        }
        const reslut: Record<string, ReadonlyArray<ContentBlockWithSection>> = {};
        Object.entries(this.contentBlocks).forEach(([key, contentblocks]) => {
            reslut[key] = contentblocks.filter(
                (block) => block.eventType?.targetItem?.key === this.selectedEventType
            );
        });
        return reslut;
    }

    setSelectedEventType(eventType: string) {
        this.selectedEventType = eventType;
    }
    setSelectedDate(date?: Date) {
        this.selectedDate = date;
    }

    get contentBlocks(): Record<string, ReadonlyArray<ContentBlockWithSection>> {
        const eventItems: DeepReadonly<ContentBlockWithSection>[] = [];

        //events from sitecore
        this.baseStore.data?.mainContents?.Item?.sections
            .filter((section) => section.key !== 'hero-banner')
            .forEach((section) => {
                const contentBlocks = section.contentBlocks.filter(
                    (b) => b.calendarEnabled?.boolValue
                );
                const blocksWithSection = contentBlocks.map((b) => withSectionPath(section.key, b));
                eventItems.push(...blocksWithSection);
            });
        //racingEventType for live schedule
        const racingEventType = this.baseStore.data?.eventTypes.find(
            (value) => value.key === 'racing'
        );
        //racingEventType for MsLiveSchedule
        const racingMsEventType = this.baseStore.data?.eventTypes.find(
            (value) => value.key === 'marksix'
        );

        if (this.settingStore.liveSchedule) {
            this.settingStore.liveSchedule?.racingScheduleList.forEach((sche) => {
                if (sche.date) {
                    const startTime = parse(
                        `${sche.date} ${sche.time}`,
                        'dd/MM/yyyy HH:mm',
                        new Date()
                    );
                    const endTime = parse(
                        `${sche.endDate} ${sche.endTime}`,
                        'dd/MM/yyyy HH:mm',
                        new Date()
                    );
                    const content: ContentBlockWithSection = {
                        key: `${format(startTime, 'yyyyMMddHHmm')}`, //use start date time as key
                        section: 'liveSchedule',
                        calendarEnabled: { boolValue: true },
                        liveSchedule: sche,
                        startDatetime: { dateValue: startTime.getTime() },
                        endDatetime: { dateValue: endTime.getTime() },
                        eventDate: { dateValue: startTime.getTime() },
                        calendarTitle: { value: sche.title },
                        calendarEventDate: startTime.getTime(),
                        eventType: { targetItem: racingEventType },
                    };
                    eventItems.push(content);
                }
            });
        }

        if (this.settingStore.MsLiveSchedule) {
            this.settingStore.MsLiveSchedule?.msScheduleList.forEach((sche) => {
                if (sche.date) {
                    const startTime = parse(
                        `${sche.date} ${sche.time}`,
                        'dd/MM/yyyy HH:mm',
                        new Date()
                    );
                    const endTime = parse(
                        `${sche.endDate} ${sche.endTime}`,
                        'dd/MM/yyyy HH:mm',
                        new Date()
                    );
                    const content: ContentBlockWithSection = {
                        key: `${format(startTime, 'yyyyMMddHHmm')}`, //use start date time as key
                        section: 'MsLiveSchedule',
                        calendarEnabled: { boolValue: true },
                        MsLiveSchedule: sche,
                        startDatetime: { dateValue: startTime.getTime() },
                        endDatetime: { dateValue: endTime.getTime() },
                        eventDate: { dateValue: startTime.getTime() },
                        calendarTitle: { value: sche.title },
                        calendarEventDate: startTime.getTime(),
                        eventType: { targetItem: racingMsEventType },
                    };
                    eventItems.push(content);
                }
            });
        }
        //handle calendar entries
        const calendarEntries = this.baseStore?.data?.calendarEntries.filter(
            (b) => b.calendarEnabled?.boolValue
        );
        if (calendarEntries) {
            calendarEntries.forEach((calendarEntry) => {
                if (calendarEntry.eventDate?.dateValue) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const content: ContentBlockWithSection | any = {
                        // id: calendarEntry.id,
                        key: calendarEntry.name, //use entry name as key
                        section: 'calendarEntry',
                        blockType: { value: 'calendarEntry' },
                        ...calendarEntry,
                    };
                    if (calendarEntry.calendarDates?.value) {
                        const calendarDates = calendarEntry.calendarDates.value;
                        content.calendarDates = { value: calendarDates };
                    }
                    eventItems.push(content);
                }
            });
        }
        //need to sort multi dates, cannot use the old method anymore.
        //sortContentBlocksByEventDate(eventItems as ContentBlockWithSection[]);
        const formatedEventItems = formatContentBlocksByDate(
            eventItems as ContentBlockWithSection[]
        ); //format events by date
        console.log('formatedEventItems', formatedEventItems);
        const sortedResult = sortContentBlocksByMultiDates(formatedEventItems);
        return sortedResult;
    }
    prevWeek() {
        this.currentWeek = subWeeks(this.currentWeek, 1);
    }
    nextWeek() {
        this.currentWeek = addWeeks(this.currentWeek, 1);
    }
    setCurrentWeek(week: Date) {
        this.currentWeek = startOfWeek(week);
    }
    setCurrentMonth(month: Date) {
        this.currentMonth = startOfMonth(month);
    }
    toggleOneWeekMode() {
        this.isOneWeekMode = !this.isOneWeekMode;
        if (this.isOneWeekMode) {
            if (this.selectedDate && isSameMonth(this.currentMonth, this.selectedDate)) {
                this.currentWeek = startOfWeek(this.selectedDate);
            } else if (this.selectedDate && isSameMonth(this.currentMonth, Date.now())) {
                this.currentWeek = startOfWeek(Date.now());
            } else {
                this.currentWeek = startOfWeek(startOfMonth(this.currentMonth));
            }
        }
    }
    setDefaults() {
        const now = new Date();
        this.selectedEventType = '';
        this.selectedDate = now;
        this.currentWeek = startOfWeek(now);
        this.currentMonth = startOfMonth(now);
        this.isOneWeekMode = true;
    }

    get eventTypes() {
        return this.baseStore.data?.eventTypes;
    }
}

// Calendar logic:
// (1) In Calendar Page:
// If start date is empty: The content do not show in calendar
// If Set start date but end date is empty: The content show in calendar but hide "Added to calendar" button;
// (2) In Content Detail page:
// Neither start date or end date is empty, hide "Added to calendar" button
function formatContentBlocksByDate(contentBlocks?: ContentBlockWithSection[]) {
    const records = {} as Record<string, ContentBlockWithSection[]>;
    contentBlocks?.forEach((current) => {
        if (current.section != 'liveSchedule' && current.section != 'MsLiveSchedule') {
            //only push start date to calendar
            //Calendar event start and end date should not be empty
            if (current.eventDate?.dateValue) {
                let isEmptyEndDate = false;
                // iOS Safari 对 new Date(current.eventDate?.dateValue) 的解析存在差异，建议使用 date-fns统一日期格
                const eventDate = toHKZone(current.eventDate?.dateValue);
                const tempDate = new Date(eventDate);
                const dateValue = toHKZone(current.eventEndDate?.dateValue ?? 0);
                //if no end date, hide addToCalendar icon
                if (dateValue.getFullYear() === 1 && current.addToCalendarEnabled)
                    isEmptyEndDate = true;
                //if endDate is empty, set it as start date 23:59
                const eventEndDate =
                    dateValue.getFullYear() === 1 ? toHKZone(tempDate.setHours(23, 59)) : dateValue;
                if (eventDate && eventEndDate && isBefore(eventDate, eventEndDate))
                    pushDateArray(eventDate, eventEndDate, current, records, isEmptyEndDate);
            }
            //handle multi event dates
            // NameValueList from Sitecore need to split with '&' first to get each NameValue item, then split with '=' to get name and value
            if (current.calendarDates?.value) {
                current.calendarDates?.value?.split('&').map((dates) => {
                    const startValue = validateAndFormatDate(
                        dates.split('=')[0].trimStart().trimEnd()
                    );
                    const endValue = dates.split('=')[1].trimStart().trimEnd();
                    //check if startValue is 'yyyy-MM-dd HH:mm'
                    if (startValue) {
                        let isEmptyEndDate = false;
                        const startDate = toHKZone(startValue);
                        const tempDate = new Date(startValue);
                        let dateValue: number | string | false;
                        if (endValue === undefined || endValue === '') {
                            if (current.addToCalendarEnabled) isEmptyEndDate = true;
                            //if endDate is empty, set it as start date 23:59
                            dateValue = tempDate.setHours(23, 59);
                        } else {
                            dateValue = validateAndFormatDate(decodeURIComponent(endValue));
                        }
                        //check endValue format
                        if (dateValue) {
                            const endDate = toHKZone(dateValue);
                            isBefore(startDate, endDate) &&
                                pushDateArray(startDate, endDate, current, records, isEmptyEndDate);
                        }
                    }
                });
            }
        } else {
            const startDate = format(new Date(current.eventDate?.dateValue || 0), 'yyyy/MM/dd');
            (records[startDate] ||= []).push(current);
        }
    });

    return records;
}
function validateAndFormatDate(dateStr: string): string | false {
    const dateRegex = /^(\d{4})-(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{2})$/;
    const match = dateStr.match(dateRegex);
    if (!match) return false;
    const [_, year, month, day, hour, minute] = match;
    const isoStr = `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')} ${hour.padStart(
        2,
        '0'
    )}:${minute}`;
    const date = new Date(isoStr);
    if (!isValid(date) || date.getFullYear() <= 1970) return false;
    return isoStr;
}
//To handle zone issue, need to transfer string date value to UTC date
function toHKZone(localTimeStr: string | number) {
    // convert string to UTC format and calculate hours offset
    if (typeof localTimeStr === 'string') {
        const uTCDate = new Date(
            format(new Date(localTimeStr), 'yyyy-MM-dd HH:mm').replace(' ', 'T').concat('Z')
        );
        const OffsetTime = new Date(uTCDate);
        return new Date(OffsetTime.setUTCHours(uTCDate.getUTCHours() - 8));
    } else {
        const utcDate = localTimeStr;
        return new Date(utcDate);
    }
}
//compare start and end date, push contentBlock with multi dates
function pushDateArray(
    startDate: Date,
    endDate: Date,
    contentBlock: ContentBlockWithSection,
    records: Record<string, ContentBlockWithSection[]>,
    isEmptyEndDate: boolean
) {
    //deepclone contentblock to set calendar event date
    const clonedBlock = structuredClone(contentBlock);
    clonedBlock.calendarEventDate = startDate.getTime();
    clonedBlock.calendarEventEndDate = endDate.getTime();
    if (isEmptyEndDate) clonedBlock.addToCalendarEnabled = { boolValue: false };
    //push start date to array
    const dateStr = format(startDate, 'yyyy/MM/dd');
    (records[dateStr] ||= []).push(clonedBlock);
}
// sort by multi dates in content block list
function sortContentBlocksByMultiDates(records: Record<string, ContentBlockWithSection[]>) {
    const sortedResult: Record<string, ContentBlockWithSection[]> = {};
    const sortedKeys = Object.keys(records).sort((a, b) => a.localeCompare(b));
    sortedKeys?.forEach((element) => {
        const sortedContentBlocks = [...records[element]].sort((a, b) => {
            // compare time
            const timeA = a.calendarEventDate || Date.now();
            const timeB = b.calendarEventDate || Date.now();
            return timeA - timeB;
        });
        sortedResult[element] = sortedContentBlocks;
    });
    return sortedResult;
}
/**
 * this function will add the key of section to content block
 * @param sectionPath key of section
 */
export function withSectionPath(
    sectionPath: string,
    contentBlock: DeepReadonly<ContentBlockFragment>
): DeepReadonly<ContentBlockWithSection> {
    sectionPath ??= '';
    return { section: sectionPath, ...contentBlock };
}

export type ContentBlockWithSection = {
    url?: string;
    id?: string;
    section: string;
    liveSchedule?: RacingScheduleItem;
    MsLiveSchedule?: RacingScheduleItem;
    key: string;
    pageTitle?: { value?: string };
    category?: CategoriesMultilistFieldFragment;
    hashTags?: HashTagsMultilistFieldFragment;
    // segmentCodes?: SegmentCodeMultilistFieldFragment;
    // accountListCodes?: AccountListMultilistFieldFragment;
    isPublished?: { boolValue?: boolean };
    startDatetime?: { dateValue?: number };
    endDatetime?: { dateValue?: number };
    editorsPick?: { boolValue?: boolean };
    eventType?: { targetItem?: CalendarEventTypesFragment };
    eventDate?: { dateValue?: number };
    eventEndDate?: { dateValue?: number };
    calendarDates?: { values?: Array<{ name: string; value: string }>; value?: string };
    calendarEventDate?: number;
    calendarEventEndDate?: number;
    calendarTitle?: { value?: string };
    calendarSubTitle?: { value?: string };
    calendarEnabled?: { boolValue?: boolean };
    calendarActionText?: { value?: string };
    addToCalendarEnabled?: { boolValue?: boolean };
    videoTitle?: { value?: string };
    videoLink?: { value?: string };
    videoPoster?: { src?: string };
    videoNative?: { boolValue?: boolean };
    videoNativePip?: { boolValue?: boolean };
    blockType?: { value?: string };
    externalHashId?: { boolValue?: boolean };
    externalAc?: { boolValue?: boolean };
    externalSeg?: { boolValue?: boolean };
    externalLink?: { value?: string };
    internalLink?: { value?: string };
    deepLink?: { value?: string };
    raceLink?: {
        targetItem?: { path: string } | { path: string } | { path: string } | { path: string };
    };
};
